import { Controller } from "@hotwired/stimulus";

const pluralize = (count, noun, suffix = "s") =>
  `${count} ${noun}${count !== 1 ? suffix : ""}`;
const pluralizeWithoutNumber = (count, noun, suffix = "s") =>
  `${noun}${count !== 1 ? suffix : ""}`;
export default class extends Controller {
  static targets = [
    "form",
    "paginatedCheckboxInput",
    "proxyCheckboxInput",
    "resourceCount",
    "pluralizedResource",
    "bulkActionButton",
    "selectAllButton",
    "clearSelectionButton",
    "resourceSelectedTotalCount",
    "resourceSelectedActiveLabel",
  ];
  static values = {
    allResourceIds: Array,
    resourceName: { type: String, default: "resource" },
    initialSelectedIds: { type: Array, default: [] },
  };

  connect() {
    this.applyInitialSelection();
    this.updateResourceCounts();
  }

  applyInitialSelection() {
    if (!this.initialSelectedIdsValue || this.initialSelectedIdsValue.length === 0)
      return;
    this.initialSelectedIdsValue.forEach((id) => {
      this.createProxyCheckboxInput("ids[]", String(id));
    });
    this.paginatedCheckboxInputTargets.forEach((checkbox) => {
      if (this.initialSelectedIdsValue.includes(parseInt(checkbox.value, 10))) {
        checkbox.checked = true;
      }
    });
    this.updateResourceCounts();
  }

  // Each bulk action is its own form, so a selected id has to be mirrored into
  // every one of them - a hidden input only travels with the form it lives in.
  createProxyCheckboxInput(name, value) {
    this.formTargets.forEach((form) => {
      if (this.findProxyCheckboxInput(form, name, value)) return;
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      input.dataset.paginatedFormTarget = "proxyCheckboxInput";
      form.appendChild(input);
    });
  }

  removeProxyCheckboxInput(name, value) {
    this.formTargets.forEach((form) => {
      this.findProxyCheckboxInput(form, name, value)?.remove();
    });
  }

  findProxyCheckboxInput(form, name, value) {
    return form.querySelector(
      `input[type="hidden"][name="${name}"][value="${CSS.escape(value)}"]`
    );
  }

  // A given id is mirrored once per form, so count the distinct values.
  get selectedCount() {
    return new Set(this.proxyCheckboxInputTargets.map((input) => input.value))
      .size;
  }

  selectAll() {
    this.allResourceIdsValue.forEach((studentId) => {
      this.createProxyCheckboxInput("ids[]", String(studentId));
    });
    this.paginatedCheckboxInputTargets.forEach((checkbox) => {
      checkbox.checked = true;
    });
    this.updateResourceCounts();
  }

  selectFiltered(e) {
    const filteredResourceIds = e.params.ids;
    filteredResourceIds.forEach((studentId) => {
      this.createProxyCheckboxInput("ids[]", String(studentId));
    });
    this.paginatedCheckboxInputTargets.forEach((checkbox) => {
      checkbox.checked = filteredResourceIds.includes(parseInt(checkbox.value));
    });
    this.updateResourceCounts();
  }

  reset() {
    this.proxyCheckboxInputTargets.forEach((input) => input.remove());
    this.paginatedCheckboxInputTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.updateResourceCounts();
  }

  updateResourceCounts() {
    if (!this.hasResourceSelectedTotalCountTarget) return;
    const count = this.selectedCount;
    if (count > 0) {
      this.resourceSelectedTotalCountTarget.textContent = `${String(
        count
      )} selected`;
      this.resourceSelectedActiveLabelTarget.classList.remove("hidden");
    } else {
      this.resourceSelectedTotalCountTarget.textContent = "";
      this.resourceSelectedActiveLabelTarget.classList.add("hidden");
    }
    this.resourceCountTargets.forEach((resourceCount) => {
      resourceCount.textContent = pluralize(count, this.resourceNameValue);
    });
    this.pluralizedResourceTargets.forEach((pluralizedResource) => {
      pluralizedResource.textContent = pluralizeWithoutNumber(
        count,
        this.resourceNameValue
      );
    });
    this.bulkActionButtonTargets.forEach((bulkActionButton) => {
      bulkActionButton.disabled = count === 0;
    });
    this.selectAllButtonTarget.disabled =
      count === this.allResourceIdsValue.length;
    this.clearSelectionButtonTarget.disabled = count === 0;
  }

  paginatedCheckboxInputTargetConnected(paginatedCheckboxInput) {
    // if paginatedCheckboxInput has a corresponding proxyCheckboxInput, check the checkbox
    const proxyCheckboxInput = this.proxyCheckboxInputTargets.find(
      (proxyCheckboxInput) => {
        return proxyCheckboxInput.value === paginatedCheckboxInput.value;
      }
    );
    if (proxyCheckboxInput) {
      paginatedCheckboxInput.checked = true;
    }
  }

  onChange(e) {
    const target = e.target;
    if (target.checked) {
      this.createProxyCheckboxInput(target.name, target.value);
    } else {
      this.removeProxyCheckboxInput(target.name, target.value);
    }
    this.updateResourceCounts();
  }
}
